import { useMemo } from "react";
import {
  effectiveInventoryTargets,
  materialsWithInventoryBounds,
} from "./inventoryTargetViews";
import { inventoryTableSx } from "./inventoryTableStyles";
import { PeriodLayout, previewPeriodCount } from "./periodLayout";
import { Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import {
  HotkeyCompliantDataGrid,
  NumberCell,
} from "components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { NameBasedTargetInventoryItem } from "store/api/generatedApi";
import { PeriodTargetsPopover } from "./PeriodTargetsPopover";
import { PeriodInventoryTargets } from "./ruleParameters";

export const InventoryTargetsTable = ({
  targets,
  materials,
  onChange,
  disabled,
  rules,
  fixedPeriods,
  periodLayout,
}: {
  targets: NameBasedTargetInventoryItem[];
  materials: string[];
  onChange: (targets: NameBasedTargetInventoryItem[]) => void;
  disabled: boolean;
  rules: PeriodInventoryTargets[];
  fixedPeriods?: Record<string, NameBasedTargetInventoryItem[]>;
  periodLayout: PeriodLayout;
}) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);
  const deployable = previewPeriodCount(periodLayout.deployable);
  const forecast = previewPeriodCount(periodLayout.forecast);
  const previewMaterials = useMemo(
    () =>
      materialsWithInventoryBounds(
        fixedPeriods ??
          effectiveInventoryTargets({ rules, deployable, forecast })
      ),
    [fixedPeriods, rules, deployable, forecast]
  );
  const names = [
    ...new Set([
      ...materials,
      ...targets.map((target) => target.material_name),
    ]),
  ];
  const rows = names.map(
    (material_name) =>
      targets.find((target) => target.material_name === material_name) ?? {
        material_name,
        min_quantity: null,
        max_quantity: null,
      }
  );
  const columns: GridColDef<NameBasedTargetInventoryItem>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => t(row.material_name),
    },
    ...(["min_quantity", "max_quantity"] as const).map((field) => ({
      field,
      headerName: `${t(field === "min_quantity" ? "targetMin" : "targetMax")}${units("mass")}`,
      type: "number" as const,
      editable: !disabled,
      minWidth: 140,
      flex: 1,
      renderCell: ({ row }: { row: NameBasedTargetInventoryItem }) => (
        <Stack direction="row" alignItems="center">
          <NumberCell value={row[field]} />
          <PeriodTargetsPopover
            material={row.material_name}
            hasBounds={previewMaterials.has(row.material_name)}
            rules={rules}
            periodLayout={periodLayout}
            fixedPeriods={fixedPeriods}
          />
        </Stack>
      ),
    })),
  ];
  return (
    <HotkeyCompliantDataGrid
      sx={inventoryTableSx}
      rows={rows}
      columns={columns}
      getRowId={(row) => row.material_name}
      disableRowSelectionOnClick
      processRowUpdate={(updated: NameBasedTargetInventoryItem) => {
        const remaining = targets.filter(
          (target) => target.material_name !== updated.material_name
        );
        onChange(
          updated.min_quantity === null && updated.max_quantity === null
            ? remaining
            : [...remaining, updated]
        );
        return updated;
      }}
    />
  );
};
